import { Link } from "react-router-dom";
import { LOGO_SYMBOL, INSTAGRAM_URL, WHATSAPP_NUMBER, whatsappLink } from "../../utils/constants";
import { useLanguage } from "../../utils/i18n";
import { MessageCircle, Instagram, Mail, MapPin } from "lucide-react";

const footerText = {
  pt: {
    tagline: "Instituto de Ocultismo, Consciência e Transformação",
    nav: "Navegação",
    contact: "Contacto",
    legal: "Legal",
    privacy: "Política de Privacidade",
    cookies: "Política de Cookies",
    terms: "Termos e Condições",
    rights: "Todos os direitos reservados.",
    disclaimer: "As práticas espirituais são de autoconhecimento e contemplativas. Não substituem cuidados médicos ou psicológicos.",
    home: "Início",
    experiences: "Experiências",
    about: "João Ramos",
    contacts: "Contactos",
  },
  en: {
    tagline: "Institute of Occultism, Consciousness & Transformation",
    nav: "Navigation",
    contact: "Contact",
    legal: "Legal",
    privacy: "Privacy Policy",
    cookies: "Cookie Policy",
    terms: "Terms & Conditions",
    rights: "All rights reserved.",
    disclaimer: "Spiritual practices are of a and self-knowledge nature. They do not replace medical or psychological care.",
    home: "Home",
    experiences: "Experiences",
    about: "Joao Ramos",
    contacts: "Contact",
  },
  es: {
    tagline: "Instituto de Ocultismo, Consciencia y Transformación",
    nav: "Navegación",
    contact: "Contacto",
    legal: "Legal",
    privacy: "Política de Privacidad",
    cookies: "Política de Cookies",
    terms: "Términos y Condiciones",
    rights: "Todos los derechos reservados.",
    disclaimer: "Las prácticas espirituales son de autoconocimiento y contemplativas. No sustituyen la atención médica o psicológica.",
    home: "Inicio",
    experiences: "Experiencias",
    about: "João Ramos",
    contacts: "Contacto",
  },
  fr: {
    tagline: "Institut d'Occultisme, Conscience et Transformation",
    nav: "Navigation",
    contact: "Contact",
    legal: "Mentions légales",
    privacy: "Politique de confidentialité",
    cookies: "Politique de cookies",
    terms: "Conditions générales",
    rights: "Tous droits réservés.",
    disclaimer: "Les pratiques spirituelles sont d'autoconnaissance et contemplatives. Elles ne remplacent pas les soins médicaux ou psychologiques.",
    home: "Accueil",
    experiences: "Experiences",
    about: "Joao Ramos",
    contacts: "Contact",
  },
  br: {
    tagline: "Instituto de Ocultismo, Consciência e Transformação",
    nav: "Navegação",
    contact: "Contato",
    legal: "Legal",
    privacy: "Política de Privacidade",
    cookies: "Política de Cookies",
    terms: "Termos e Condições",
    rights: "Todos os direitos reservados.",
    disclaimer: "As práticas espirituais são de autoconhecimento e contemplativas. Não substituem cuidados médicos ou psicológicos.",
    home: "Início",
    experiences: "Experiências",
    about: "João Ramos",
    contacts: "Contato",
  },
};

export const Footer = () => {
  const { language } = useLanguage();
  const t = footerText[language] || footerText.pt;

  return (
    <footer className="bg-[var(--black-graphite)] border-t border-[var(--border-subtle)]" data-testid="footer">
      <div className="max-w-[1200px] mx-auto px-5 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img src={LOGO_SYMBOL} alt="Sophitris" className="h-10 logo-glow" />
              <span className="text-sm font-semibold tracking-[0.2em] text-[var(--gold)] uppercase">SOPHITRIS</span>
            </Link>
            <p className="text-[var(--text-muted)] text-sm leading-relaxed mb-4">{t.tagline}</p>
            <div className="flex items-center gap-1.5 text-[var(--text-muted)] text-xs">
              <MapPin size={12} />
              <span>Lisboa, Portugal</span>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-[var(--gold)] text-[0.6875rem] tracking-[0.2em] uppercase mb-5 font-semibold">{t.nav}</h4>
            <div className="flex flex-col gap-2.5">
              <Link to="/" className="text-sm text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors">{t.home}</Link>
              <Link to="/experiencias" className="text-sm text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors">{t.experiences}</Link>
              <Link to="/sobre" className="text-sm text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors">{t.about}</Link>
              <Link to="/contactos" className="text-sm text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors">{t.contacts}</Link>
              <Link to="/testemunhos" className="text-sm text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors">{language === "en" ? "Testimonials" : language === "fr" ? "Témoignages" : language === "es" ? "Testimonios" : "Testemunhos"}</Link>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-[var(--gold)] text-[0.6875rem] tracking-[0.2em] uppercase mb-5 font-semibold">{t.contact}</h4>
            <div className="flex flex-col gap-3">
              <a href={whatsappLink("contacto")} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors">
                <MessageCircle size={14} /> +351 912 010 025
              </a>
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors">
                <Instagram size={14} /> @sophitrisportugal
              </a>
              <a href="mailto:sophitris@gmail.com" className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors">
                <Mail size={14} /> sophitris@gmail.com
              </a>
            </div>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-[var(--gold)] text-[0.6875rem] tracking-[0.2em] uppercase mb-5 font-semibold">{t.legal}</h4>
            <div className="flex flex-col gap-2.5">
              <Link to="/privacidade" className="text-sm text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors">{t.privacy}</Link>
              <Link to="/cookies" className="text-sm text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors">{t.cookies}</Link>
              <Link to="/termos" className="text-sm text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors">{t.terms}</Link>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 pt-6 border-t border-[var(--border-subtle)]">
          <p className="text-[var(--text-muted)] text-xs leading-relaxed mb-3">{t.disclaimer}</p>
          <p className="text-[var(--text-muted)] text-xs">&copy; {new Date().getFullYear()} SOPHITRIS. {t.rights}</p>
        </div>
      </div>
    </footer>
  );
};
